import { Order } from '../types';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';

// GÜVENLİK: User input'larını güvenli hale getir (HTML template'ini bozmadan)
const sanitizeUserInput = (input: string | undefined): string => {
  if (!input) return '';
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [], // Hiçbir HTML tag'ine izin verme
    ALLOWED_ATTR: []
  });
};

interface ReceiptSettings {
  receiptTitle: string;
  receiptSubtitle: string;
  receiptPhone: string;
  receiptTaxNumber: string;
  receiptFooter: string;
  receiptDateLabel: string;
  receiptTimeLabel: string;
  receiptOrderNumberLabel: string;
  receiptOrderDetailsLabel: string;
  receiptPaymentLabel: string;
  receiptPaymentCash: string;
  receiptPaymentCard: string;
  receiptSubtotalLabel: string;
  receiptTaxLabel: string;
  receiptTotalLabel: string;
  receiptQuantityLabel: string;
  receiptShowTaxDetails: boolean;
  receiptShowPaymentType: boolean;
  receiptShowOrderNumber: boolean;
  receiptShowDateTime: boolean;
}

/**
 * Standart termal yazıcı uyumlu fiş yazdırma fonksiyonu
 * Tüm uygulamada kullanılacak tek standart format
 */
// Basit printReceipt fonksiyonu - backward compatibility için
export const printReceipt = (order: Order) => {
  const receiptSettings: ReceiptSettings = {
    receiptTitle: '🏪 GoldCafeT OrderSystem',
    receiptSubtitle: '',
    receiptPhone: '',
    receiptTaxNumber: '',
    receiptFooter: '*** Teşekkür Ederiz ***\nTekrar bekleriz!',
    receiptDateLabel: 'Tarih:',
    receiptTimeLabel: 'Saat:',
    receiptOrderNumberLabel: 'Sipariş No:',
    receiptOrderDetailsLabel: 'SİPARİŞ DETAYLARI',
    receiptPaymentLabel: 'Ödeme:',
    receiptPaymentCash: 'NAKİT',
    receiptPaymentCard: 'KART',
    receiptSubtotalLabel: 'Ara Toplam (KDV Hariç):',
    receiptTaxLabel: 'KDV:',
    receiptTotalLabel: 'TOPLAM (KDV Dahil):',
    receiptQuantityLabel: 'x',
    receiptShowTaxDetails: true,
    receiptShowPaymentType: true,
    receiptShowOrderNumber: true,
    receiptShowDateTime: true
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return printStandardReceipt(order, receiptSettings, formatPrice);
};

export const printStandardReceipt = (
  order: Order, 
  receiptSettings: ReceiptSettings,
  formatPrice: (amount: number) => string
) => {
  // Yeni pencerede fiş açılacak - 58mm genişlik için optimize edilmiş
  const receiptWindow = window.open('', '_blank', 'width=300,height=800');
  if (!receiptWindow) {
    toast.error('Pop-up engellendi. Lütfen pop-upları etkinleştirin.');
    return;
  }

  // Standart termal yazıcı uyumlu fiş formatı
  const receiptHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Fiş #${order.id}</title>
      <style>
        @media print {
          @page {
            size: 58mm auto;
            margin: 2mm 1mm 1mm 1mm;
          }
          body {
            font-size: 14px !important;
            line-height: 1.2 !important;
            font-family: 'Courier New', monospace !important;
            color: black !important;
            background: white !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 2mm !important;
            box-sizing: border-box !important;
          }
          .receipt-container { 
            width: 100% !important; 
            max-width: 54mm !important;
            margin: 0 auto !important;
            padding: 0 !important;
          }
          .receipt-title { font-size: 16px !important; font-weight: bold !important; }
          .receipt-info { font-size: 12px !important; }
          .order-info { font-size: 12px !important; }
          .section-title { font-size: 14px !important; font-weight: bold !important; }
          .item-name { font-size: 12px !important; }
          .item-price { font-size: 12px !important; }
          .item-details { font-size: 11px !important; }
          .item-tax { font-size: 10px !important; }
          .item-notes { font-size: 10px !important; }
          .totals { font-size: 12px !important; }
          .total-final { font-size: 14px !important; font-weight: bold !important; }
          .receipt-footer { font-size: 11px !important; }
        }
        
        body {
          font-family: 'Courier New', monospace;
          font-size: 14px;
          line-height: 1.2;
          color: black;
          background: white;
          margin: 0;
          padding: 8px;
          width: 100%;
          box-sizing: border-box;
        }
        
        .receipt-container {
          width: 100%;
          max-width: 58mm;
          text-align: center;
          margin: 0 auto;
          padding: 0;
        }
        
        .receipt-header {
          text-align: center;
          margin-bottom: 8px;
        }
        
        .receipt-title {
          font-weight: bold;
          font-size: 16px;
          margin-bottom: 4px;
        }
        
        .receipt-info {
          font-size: 12px;
          margin-bottom: 2px;
        }
        
        .receipt-section {
          margin: 6px 0;
          border-bottom: 1px dashed black;
          padding-bottom: 4px;
        }
        
        .order-info {
          text-align: left;
          font-size: 12px;
        }
        
        .order-info div {
          display: flex;
          justify-content: space-between;
          margin-bottom: 2px;
        }
        
        .section-title {
          font-weight: bold;
          text-align: center;
          font-size: 14px;
          margin: 6px 0 4px 0;
        }
        
        .item {
          text-align: left;
          margin-bottom: 4px;
          border-bottom: 1px dotted #ccc;
          padding-bottom: 3px;
        }
        
        .item-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1px;
        }
        
        .item-name {
          font-weight: bold;
          font-size: 12px;
          flex: 1;
        }
        
        .item-price {
          font-weight: bold;
          font-size: 12px;
          text-align: right;
        }
        
        .item-details {
          font-size: 11px;
          margin: 1px 0;
        }
        
        .item-tax {
          font-size: 10px;
          color: #666;
          margin: 1px 0;
          text-align: right;
        }
        
        .item-notes {
          font-size: 10px;
          color: #0066cc;
          margin: 1px 0;
          font-style: italic;
        }
        
        .totals {
          text-align: left;
          font-size: 12px;
          margin: 6px 0;
        }
        
        .totals div {
          display: flex;
          justify-content: space-between;
          margin-bottom: 2px;
        }
        
        .total-final {
          font-weight: bold;
          font-size: 14px;
          border-top: 1px solid black;
          padding-top: 3px;
          margin-top: 3px;
        }
        
        .receipt-footer {
          text-align: center;
          font-size: 11px;
          margin-top: 8px;
          font-weight: bold;
        }
      </style>
    </head>
    <body>
      <div class="receipt-container">
        <!-- Header -->
        <div class="receipt-header">
          <div class="receipt-title">${sanitizeUserInput(receiptSettings.receiptTitle) || '🏪 GoldCafeT OrderSystem'}</div>
          ${receiptSettings.receiptSubtitle ? `<div class="receipt-info">${sanitizeUserInput(receiptSettings.receiptSubtitle)}</div>` : ''}
          ${receiptSettings.receiptPhone ? `<div class="receipt-info">${sanitizeUserInput(receiptSettings.receiptPhone)}</div>` : ''}
          ${receiptSettings.receiptTaxNumber ? `<div class="receipt-info">${sanitizeUserInput(receiptSettings.receiptTaxNumber)}</div>` : ''}
        </div>

        <!-- Order Info -->
        <div class="receipt-section">
          <div class="order-info">
            <div><span>${sanitizeUserInput(receiptSettings.receiptDateLabel) || 'Tarih:'}</span><span>${new Date(order.paid_at || order.created_at).toLocaleDateString('tr-TR')}</span></div>
            <div><span>${sanitizeUserInput(receiptSettings.receiptTimeLabel) || 'Saat:'}</span><span>${new Date(order.paid_at || order.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span></div>
            <div><span>${sanitizeUserInput(receiptSettings.receiptOrderNumberLabel) || 'Sipariş No:'}</span><span>#${order.id}</span></div>
          </div>
        </div>

        <!-- Items -->
        <div class="section-title">${sanitizeUserInput(receiptSettings.receiptOrderDetailsLabel) || 'SİPARİŞ DETAYLARI'}</div>
        
        ${order.items?.map(item => {
          const itemTotal = item.quantity * item.unit_price;
          const taxAmount = item.product?.tax_rate ? (itemTotal * item.product.tax_rate) / 100 : 0;
          
          return `
            <div class="item">
              <div class="item-row">
                <div class="item-name">${sanitizeUserInput(item.product?.name) || 'Bilinmeyen Ürün'}</div>
                <div class="item-price">${formatPrice(itemTotal)}</div>
              </div>
              <div class="item-details">${item.quantity} ${sanitizeUserInput(receiptSettings.receiptQuantityLabel) || 'x'} ${formatPrice(item.unit_price)}</div>
              ${item.product?.tax_rate && item.product.tax_rate > 0 ? 
                `<div class="item-tax">KDV: ${formatPrice(taxAmount)}</div>` : ''
              }
              ${item.notes ? `<div class="item-notes">📝 ${sanitizeUserInput(item.notes)}</div>` : ''}
            </div>
          `;
        }).join('') || '<div>Ürün detayları yüklenemedi</div>'}

        <!-- Totals -->
        <div class="totals">
          ${(() => {
            // Ödül ürünlerini hariç tut - Fiyata Dahil Mod (Belçika)
            const grandTotal = order.items?.reduce((sum, item) => {
              if (item.notes && item.notes.includes('🎁 ÖDÜL')) return sum;
              return sum + (item.quantity * item.unit_price);
            }, 0) || 0;
            
            const taxTotal = order.items?.reduce((sum, item) => {
              if (item.notes && item.notes.includes('🎁 ÖDÜL')) return sum;
              const itemTotal = item.quantity * item.unit_price;
              const taxRate = item.product?.tax_rate || 0;
              const taxAmount = itemTotal - (itemTotal / (1 + taxRate / 100));
              return sum + taxAmount;
            }, 0) || 0;
            
            const subtotal = grandTotal - taxTotal; // Net tutar (vergisiz)
            
            return `
              <div><span>${sanitizeUserInput(receiptSettings.receiptSubtotalLabel) || 'Net Tutar:'}</span><span>${formatPrice(subtotal)}</span></div>
              <div><span>${sanitizeUserInput(receiptSettings.receiptTaxLabel) || 'KDV:'}</span><span>${formatPrice(taxTotal)}</span></div>
              <div class="total-final"><span>${sanitizeUserInput(receiptSettings.receiptTotalLabel) || 'TOPLAM:'}</span><span>${formatPrice(grandTotal)}</span></div>
              ${receiptSettings.receiptShowPaymentType ? `<div><span>${sanitizeUserInput(receiptSettings.receiptPaymentLabel) || 'Ödeme:'}</span><span>${order.payment_type === 'nakit' ? (sanitizeUserInput(receiptSettings.receiptPaymentCash) || 'NAKİT') : (order.payment_type === 'qr' ? 'QR' : (sanitizeUserInput(receiptSettings.receiptPaymentCard) || 'KART'))}</span></div>` : ''}
            `;
          })()}
        </div>

        <!-- Footer -->
        <div class="receipt-footer">
          ${receiptSettings.receiptFooter ? receiptSettings.receiptFooter.split('\n').map(line => `<div>${sanitizeUserInput(line)}</div>`).join('') : `
            <div>*** Teşekkür Ederiz ***</div>
            <div>Tekrar bekleriz!</div>
          `}
        </div>
      </div>
      
      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
          }, 500);
        };
      </script>
    </body>
    </html>
  `;

  // GÜVENLİK: HTML template'i bozmuyor, sadece user input'ları güvenli
  receiptWindow.document.write(receiptHTML);
  receiptWindow.document.close();
};
